import { useEffect, useRef } from "react";

type OverlayEntry = {
  id: string;
  close: () => void;
};

const stack: OverlayEntry[] = [];
let listenerAttached = false;
let ignoreNextPop = false;

function ensureListener() {
  if (listenerAttached || typeof window === "undefined") return;
  listenerAttached = true;
  window.addEventListener("popstate", () => {
    if (ignoreNextPop) {
      ignoreNextPop = false;
      return;
    }
    const top = stack.pop();
    if (top) {
      top.close();
    }
  });
}

function pushOverlay(id: string, close: () => void) {
  ensureListener();
  // Avoid duplicate push if already top
  if (stack.length && stack[stack.length - 1].id === id) {
    stack[stack.length - 1].close = close;
    return;
  }
  stack.push({ id, close });
  window.history.pushState({ __thaaiOverlay: id }, "");
}

function removeOverlay(id: string) {
  const top = stack[stack.length - 1];
  if (top?.id === id) {
    stack.pop();
    ignoreNextPop = true;
    window.history.back();
    return;
  }
  const idx = stack.findIndex((e) => e.id === id);
  if (idx >= 0) stack.splice(idx, 1);
}

/**
 * When `open` becomes true, push a history entry. Mobile/PWA Back (popstate)
 * closes this overlay instead of leaving the app. Programmatic close pops history.
 */
export function useOverlayHistory(
  open: boolean,
  onClose: () => void,
  overlayId: string
) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const wasOpenRef = useRef(false);
  const closedByPopRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      closedByPopRef.current = false;
      pushOverlay(overlayId, () => {
        closedByPopRef.current = true;
        onCloseRef.current();
      });
      wasOpenRef.current = true;
    } else if (!open && wasOpenRef.current) {
      if (!closedByPopRef.current) {
        removeOverlay(overlayId);
      }
      closedByPopRef.current = false;
      wasOpenRef.current = false;
    }
  }, [open, overlayId]);

  useEffect(() => {
    return () => {
      if (wasOpenRef.current && !closedByPopRef.current) {
        removeOverlay(overlayId);
        wasOpenRef.current = false;
      }
    };
  }, [overlayId]);
}
